import { PromotionFamilyGrouping } from '../promotion-display-utils';
import { ProductAggregate } from './aggregate-cart';
import { calculateBuyXPayYForAggregate } from './calculators/buy-x-pay-y-aggregate.calculator';
import { calculateNthUnitForAggregate } from './calculators/nth-unit-aggregate.calculator';
import { calculateProgressiveForAggregate } from './calculators/progressive-aggregate.calculator';
import { FamilyOutcome, PromotionFamily } from './family-outcome.types';

const FAMILY_ORDER: Record<PromotionFamily, number> = {
  buyXPayY: 0,
  nthUnit: 1,
  progressive: 2,
};

/**
 * Picks the family with the largest `totalDiscount`. Tied totals fall back to
 * the canonical family order (buyXPayY → nthUnit → progressive). Returns null
 * when no family produces a positive discount.
 */
export function pickWinningFamily(candidates: FamilyOutcome[]): FamilyOutcome | null {
  const active = candidates.filter(c => c.totalDiscount > 0);
  if (active.length === 0) {
    return null;
  }
  const sorted = [...active].sort((a, b) => {
    if (b.totalDiscount !== a.totalDiscount) {
      return b.totalDiscount - a.totalDiscount;
    }
    return FAMILY_ORDER[a.family] - FAMILY_ORDER[b.family];
  });
  return sorted[0];
}

/**
 * Runs the three family calculators against a product aggregate and resolves
 * the best-of winner. The composition (calculators + best-of) is shared between
 * `applyToCart` (live cart math) and `calculateUpsellHints` (simulating S(q+Δ)).
 */
export function resolveProductFamilyWinner(
  families: PromotionFamilyGrouping,
  aggregate: ProductAggregate,
): { totalDiscount: number; winner: FamilyOutcome | null } {
  const candidates = [
    calculateBuyXPayYForAggregate(families.buyXPayYOffers, aggregate),
    calculateNthUnitForAggregate(families.nthUnits, aggregate),
    calculateProgressiveForAggregate(families.progressiveTiers, aggregate),
  ];
  const winner = pickWinningFamily(candidates);
  return { totalDiscount: winner?.totalDiscount ?? 0, winner };
}
